import { Checkbox } from '@mantine/core';
import { useMemo } from 'react';
import type { MantineColumn } from '../../../../../react-commons/components/table/MantineTable';
import { useNestedDialogFunction } from '../../../../../react-commons/dialog-provider/UseNestedDialogFunction';
import { ControlledInputs } from '../../../../../react-commons/utils/read-only-inputs/controlledInputs';
import { ResourceType } from '../../../resource/enumerations/ResourceType';
import {
    defaultProjectUserRoleResource,
    type ProjectUserRoleResource,
    useProjectUserRoleActions,
} from '../../../resource/resources/persistables/user/projectUserRoleResource';
import { ProjectUserRoleCreateAndEditDialog } from '../../page-components/project-user-management/project-user-role/ProjectUserRoleCreateAndEditDialog';
import { useDeleteResourceDialogFunction } from '../../reusable-components/dialogs/UseDeleteResourceDialogFunction';
import { CrudValidationTable } from '../../reusable-components/tables/CrudValidationTable';

export const ProjectUserRolesPageTab = ({ projectId }: { projectId: string }) => {
    const nestedDialog = useNestedDialogFunction();
    const deleteResourceDialogFunction = useDeleteResourceDialogFunction();

    const projectUserRoleActions = useProjectUserRoleActions();
    const projectUserRoles = projectUserRoleActions.useGetAllResources(projectId);
    const deleteProjectUserRole = projectUserRoleActions.useDeleteResource();

    const openAddDialog = () => {
        nestedDialog.showNestedDialog(
            <ProjectUserRoleCreateAndEditDialog
                label={'Add Project User Role'}
                projectUserRole={defaultProjectUserRoleResource}
                onClose={nestedDialog.closeNestedDialog}
                parentId={projectId}
            />
        );
    };

    const openEditDialog = (projectUserRole: ProjectUserRoleResource) => {
        nestedDialog.showNestedDialog(
            <ProjectUserRoleCreateAndEditDialog
                label={'Edit Project User Role'}
                projectUserRole={projectUserRole}
                onClose={nestedDialog.closeNestedDialog}
                parentId={projectId}
            />
        );
    };

    const deleteRole = (id: string, name: string) => {
        deleteResourceDialogFunction.deleteResource(id, name, ResourceType.PROJECT_USER_ROLE, deleteProjectUserRole);
    };

    const sortedProjectUserRoles = useMemo(() => {
        if (!projectUserRoles.data) {
            return [];
        }
        return projectUserRoles.data.toSorted((a, b) => a.name.localeCompare(b.name));
    }, [projectUserRoles.data]);

    const columns = useMemo((): Array<MantineColumn<ProjectUserRoleResource>> => {
        return [
            {
                type: 'string',
                header: 'Name',
                accessorKey: 'name',
                Header: null,
                Cell: ({ row }) => (
                    <ControlledInputs.String
                        nullable={false}
                        readOnly={true}
                        value={row.original.name}
                        style={{ width: '100%' }}
                    />
                ),
            },
            {
                type: 'boolean',
                header: 'Manager',
                accessorKey: 'isManager',
                Header: null,
                Cell: ({ row }) => <Checkbox checked={row.original.isManager ?? false} />,
            },
            {
                type: 'boolean',
                header: 'Process Admin',
                accessorKey: 'isProcessAdmin',
                Header: null,
                Cell: ({ row }) => <Checkbox checked={row.original.isProcessAdmin ?? false} />,
            },
            {
                type: 'boolean',
                header: 'Reader',
                accessorKey: 'isReader',
                Header: null,
                Cell: ({ row }) => <Checkbox checked={row.original.isReader ?? false} />,
            },
        ];
    }, []);

    return (
        <div className={'main-window-content'}>
            <CrudValidationTable
                columns={columns}
                data={sortedProjectUserRoles}
                label={'Project User Roles'}
                onAdd={openAddDialog}
                onDelete={(resource) => {
                    deleteRole(resource.id, resource.name);
                }}
                onEdit={(resource) => {
                    const role = projectUserRoles.data?.find((r) => r.id === resource.id);
                    if (role) {
                        openEditDialog(role);
                    }
                }}
                initialPageSize={10}
            />
        </div>
    );
};
